import React, { ReactElement } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import loadable from '@loadable/component';
import {
  RouteAuthenticator,
  routeAuthenticatorRouteProps,
  AuthenticatedRoute,
  UnauthenticatedRoute as Route,
  useCouponCodeCatcher,
} from '@blocs.auth';
import { useTranslation } from '@blocs.i18n';
import { useRemoveSplashLoader } from '@minecraft.utils';
import Loader from '@minecraft.loader';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { PaywallLocation, useRegistrationLocation } from '@blocs.paywall';
import { ProfileRedirect } from 'src/routes/profile/profile-redirect';

const PageShell = loadable(() => import(/* webpackChunkName: "private-page" */ 'components/core/PageShell'));

/**
 * @deprecated This component is deprecated. Please use PageShell instead.
 */
const Page = loadable(() => import(/* webpackChunkName: "talent-page-component" */ 'components/core/Page'));
const Login = loadable(() => import(/* webpackChunkName: "talent-login" */ 'src/routes/login'));
const TalentsCreate = loadable(() => import(/* webpackChunkName: "talent-create" */ 'src/routes/talents/create'));
const TalentDetail = loadable(() => import(/* webpackChunkName: "talent-detail" */ 'src/routes/talents/detail'));
const TalentsCopy = loadable(() => import(/* webpackChunkName: "talent-copy" */ 'src/routes/talents/copy'));
const TalentsCopySelect = loadable(
  () => import(/* webpackChunkName: "talent-copy-select" */ 'src/routes/talents/copy-select')
);
const TalentsName = loadable(() => import(/* webpackChunkName: "talent-name" */ 'src/routes/talents/name'));
const TalentsSelect = loadable(
  () => import(/* webpackChunkName: "talent-talents" */ 'src/routes/talents/talents-select')
);
const Project = loadable(() => import(/* webpackChunkName: "talent-project" */ 'src/routes/project'));
const CustomizeSubmission = loadable(
  () => import(/* webpackChunkName: "talent-customize-submission" */ 'src/routes/customize-submission')
);
const Role = loadable(() => import(/* webpackChunkName: "talent-role" */ 'src/routes/role'));
const Request = loadable(() => import(/* webpackChunkName: "talent-request" */ 'src/routes/request'));
const MediaRequest = loadable(() => import(/* webpackChunkName: "talent-media-request" */ 'src/routes/media-request'));
const AccountDetails = loadable(
  () => import(/* webpackChunkName: "talent-account-details" */ 'components/Accounts/AccountDetails')
);
const ProfileCreate = loadable(
  () => import(/* webpackChunkName: "talent-profile-create" */ 'src/routes/profile/ProfileCreate')
);
const ProfileUploadPhoto = loadable(
  () => import(/* webpackChunkName: "talent-upload-photo" */ 'src/routes/profile/upload-photo')
);
const ProfileSkipStep = loadable(
  () => import(/* webpackChunkName: "talent-skip-step" */ 'src/routes/profile/skip-step')
);
const ProfileSettings = loadable(
  () => import(/* webpackChunkName: "talent-profile-settings" */ 'src/routes/profile/settings/settings')
);
const ProfileSettingsProfiles = loadable(
  () => import(/* webpackChunkName: "talent-settings-profiles" */ 'src/routes/profile/settings/profiles')
);
const ProfileSettingsArtists = loadable(
  () => import(/* webpackChunkName: "talent-settings-artists" */ 'src/routes/profile/settings/artists')
);
const ProfileEdit = loadable(() => import(/* webpackChunkName: "talent-profile-edit" */ 'src/routes/profile/edit'));
const ProfilePreview = loadable(
  () => import(/* webpackChunkName: "talent-profile-preview" */ 'src/routes/profile/preview')
);
const RosterInvite = loadable(() => import(/* webpackChunkName: "talent-roster-invite" */ 'src/routes/rosterInvite'));
const AcceptProfileAccess = loadable(
  () => import(/* webpackChunkName: "talent-accept-profile-access" */ 'src/routes/accept-profile-access')
);
const ArtistsCreate = loadable(
  () => import(/* webpackChunkName: "talent-artists-create" */ 'src/routes/artists/create')
);
const ArtistsCreatePersonal = loadable(
  () => import(/* webpackChunkName: "talent-artists-create-personal" */ 'src/routes/artists/createPersonal')
);
const ArtistsDetail = loadable(
  () => import(/* webpackChunkName: "talent-artists-select" */ 'src/routes/artists/detail')
);
const ArtistsProfiles = loadable(
  () => import(/* webpackChunkName: "talent-artists-profiles" */ 'src/routes/artists/profiles')
);
const ArtistsSelect = loadable(
  () => import(/* webpackChunkName: "talent-artists" */ 'src/routes/artists/artists-select')
);
const ScheduleInvite = loadable(
  () => import(/* webpackChunkName: "talent-schedule-invite" */ 'src/routes/schedule-invite')
);
const NotificationSettingsArtist = loadable(
  () => import(/* webpackChunkName: "talent-notif-settings-artist" */ 'src/routes/notification-settings/artist')
);
const NotificationSettingsProjects = loadable(
  () => import(/* webpackChunkName: "talent-notif-settings-projects" */ 'src/routes/notification-settings/projects')
);
const NotificationSettings = loadable(
  () => import(/* webpackChunkName: "talent-notif-settings" */ 'src/routes/notification-settings/notification-settings')
);
const MediaBank = loadable(() => import(/* webpackChunkName: "talent-media-bank" */ 'src/routes/media-bank'));
const CastingBillboard = loadable(
  () => import(/* webpackChunkName: "talent-billboard" */ 'src/routes/casting-billboard')
);
const SavedSearches = loadable(
  () => import(/* webpackChunkName: "talent-saved-searches" */ 'src/routes/saved-searches')
);
const EmailReset = loadable(() => import(/* webpackChunkName: "talent-email-reset" */ 'src/routes/email-reset'));
const LoginAdmin = loadable(() => import(/* webpackChunkName: "talent-login-admin" */ 'src/routes/loginAdmin'));
const Register = loadable(() => import(/* webpackChunkName: "talent-register" */ 'src/routes/register'));
const ResetEmail = loadable(() => import(/* webpackChunkName: "talent-reset-email" */ 'src/routes/resetEmail'));
const Submissions = loadable(() => import(/* webpackChunkName: "talent-submissions" */ 'src/routes/submissions'));
const VerifyEmail = loadable(() => import(/* webpackChunkName: "talent-verify-email" */ 'src/routes/verify-email'));
const YourAlerts = loadable(() => import(/* webpackChunkName: "talent-your-alerts" */ 'src/routes/your-alerts'));

const YourAuditions = loadable(
  () => import(/* webpackChunkName: "your-insights" */ 'components/Insights/YourAuditions')
);

const PublicProfile = loadable(
  () => import(/* webpackChunkName: "talent-public-profile" */ 'src/routes/publicProfile')
);
const EditUploadedPhoto = loadable(
  () => import(/* webpackChunkName: "talent-edit-uploaded-photo" */ 'src/routes/edit-uploaded-photo')
);
const EditPrimaryPhoto = loadable(
  () => import(/* webpackChunkName: "talent-edit-primary-photo" */ 'src/routes/profile/edit-primary-photo')
);
const EditThumbnail = loadable(
  () => import(/* webpackChunkName: "talent-edit-thumbnail" */ 'src/routes/profile/edit-thumbnail')
);
const MembershipOverview = loadable(
  () => import(/* webpackChunkName: "talent-membership" */ 'src/routes/membership/membership-overview')
);
const EditBillingInfo = loadable(
  () => import(/* webpackChunkName: "talent-edit-billing-info" */ 'src/routes/membership/edit-billing-info')
);
const DefaultRedirect = loadable(
  () => import(/* webpackChunkName: "talent-default-redirect" */ 'src/routes/default-redirect')
);
const Maintenance = loadable(
  () => import(/* webpackChunkName: "talent-maintenance" */ 'src/routes/splash-screens/maintenance')
);
const SubmissionTrends = loadable(
  () => import(/* webpackChunkName: "submission-trends" */ 'components/Insights/SubmissionTrends')
);
const RoleTracker = loadable(() => import(/* webpackChunkName: "role-tracker" */ 'components/Insights/RoleTracker'));

const SubmissionCompletion = loadable(
  () => import(/* webpackChunkName: "talent-submission-completion" */ 'src/routes/submission-completion')
);
const NotFound = loadable(() => import(/* webpackChunkName: "talent-not-found" */ '@blocs.not-found'), {
  resolveComponent: (components) => components.NotFound,
});

export const Routes = (): ReactElement => {
  const { t } = useTranslation();
  const { isMobileRegistration } = useRegistrationLocation({ paywallLocation: PaywallLocation.SIGNUP });
  const submissionTrends = useFeatureExperiment(FEATURE_FLAGS.SUBMISSION_TRENDS);
  const roleTracker = useFeatureExperiment(FEATURE_FLAGS.WEB_ROLE_TRACKER);
  const yourAuditions = useFeatureExperiment(FEATURE_FLAGS.AUDITIONS);
  useRemoveSplashLoader();
  const { complete } = useCouponCodeCatcher();

  if (!complete) {
    return null;
  }

  return (
    <Switch>
      <Route exact path="/public-profile/:guid">
        <PublicProfile />
      </Route>
      <Route exact path="/maintenance">
        <Maintenance fallback={<Loader />} />
      </Route>
      <Route {...routeAuthenticatorRouteProps}>
        <RouteAuthenticator redirectTo="/default-redirect" />
      </Route>
      <AuthenticatedRoute exact path="/profile">
        <Page>
          <ProfileRedirect />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/default-redirect">
        <PageShell>
          <DefaultRedirect />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/">
        <Redirect to="/login" />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/login">
        <Page withoutNavigation>
          <Login fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/talents/create">
        <Page>
          <TalentsCreate fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/talents/:artistId">
        <Page>
          <TalentDetail fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profiles/:profileId/copy">
        <Page>
          <TalentsCopy fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/project/:projectId/role/:roleId/profiles/:profileId/copy">
        <Page>
          <TalentsCopy fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profiles/:profileId/copy-select">
        <Page>
          <TalentsCopySelect fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/project/:projectId/role/:roleId/profiles/:profileId/copy-select">
        <Page>
          <TalentsCopySelect fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/talents/:artistId/name">
        <Page>
          <TalentsName fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/talents">
        <Page pageTitle={t('talent:routes.pageTitles.manageTalent')} blueBg>
          <TalentsSelect fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/project/:projectId">
        <PageShell>
          <Project fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/project/:projectId/role/:roleId/customize-submission">
        <PageShell>
          <CustomizeSubmission fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>

      <AuthenticatedRoute exact path="/project/:projectId/role/:roleId/submission-completion">
        <PageShell>
          <SubmissionCompletion fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>

      <AuthenticatedRoute exact path="/project/:projectId/role/:roleId">
        <PageShell>
          <Role fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/request/:requestId">
        <Page>
          <Request fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/media-request/:requestMediaId">
        <Page>
          <MediaRequest fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/account-settings/details">
        <Page innerTheme="dark">
          <AccountDetails fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/account-settings/notifications/:artistId">
        <Page innerTheme="dark">
          <NotificationSettingsArtist fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/account-settings/notifications/:artistId/projects">
        <Page innerTheme="dark">
          <NotificationSettingsProjects fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/account-settings/notifications">
        <Page innerTheme="dark">
          <NotificationSettings fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/verify-age" shouldHideZendesk={isMobileRegistration}>
        <PageShell withoutNavigation>
          <ProfileCreate fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/create" shouldHideZendesk={isMobileRegistration}>
        <PageShell withoutNavigation>
          <ProfileCreate fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/create/:profileId" shouldHideZendesk={isMobileRegistration}>
        <PageShell withoutNavigation>
          <ProfileCreate fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/upload-photo">
        <Page withoutNavigation>
          <ProfileUploadPhoto fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/edit-primary-photo">
        <Page>
          <EditPrimaryPhoto fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/edit-thumbnail">
        <Page>
          <EditThumbnail fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/upload-video">
        <Page withoutNavigation>
          <ProfileSkipStep fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/upload-audio">
        <Page withoutNavigation>
          <ProfileSkipStep fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile-settings/talents/:artistId/profiles/:profileId">
        <Page innerTheme="dark">
          <ProfileSettings fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/membership-billing">
        <Page innerTheme="dark">
          <MembershipOverview fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/membership-billing/edit-billing-info">
        <Page innerTheme="dark">
          <EditBillingInfo fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/membership-billing/:artistId/subscription-summary/:planId">
        <Page asPaywallLocation={PaywallLocation.MEMBERSHIP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/membership-billing/:artistId/checkout">
        <Page asPaywallLocation={PaywallLocation.MEMBERSHIP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/membership-billing/:artistId/purchase-success">
        <Page asPaywallLocation={PaywallLocation.MEMBERSHIP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile-settings/talents/:artistId/profiles">
        <Page innerTheme="dark">
          <ProfileSettingsProfiles fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile-settings/talents">
        <Page innerTheme="dark">
          <ProfileSettingsArtists fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute shouldHideZendesk={isMobileRegistration} exact path="/profile/:profileId/subscriptions">
        <Page withoutNavigation showLogoutWhenHiddenNav asPaywallLocation={PaywallLocation.SIGNUP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/purchase-success">
        <Page withoutNavigation asPaywallLocation={PaywallLocation.SIGNUP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/subscriptions/:planId">
        <Page withoutNavigation asPaywallLocation={PaywallLocation.SIGNUP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/checkout">
        <Page withoutNavigation asPaywallLocation={PaywallLocation.SIGNUP} />
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId/:editView">
        <Page>
          <ProfileEdit fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/profile/:profileId">
        <Page>
          <ProfilePreview fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      {/* /roster-invite/:token is the route for a user who gets a roster invite email and clicks "I have an existing account" link */}
      <AuthenticatedRoute exact path="/roster-invite/:token">
        <Page withoutNavigation>
          <RosterInvite fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      {/*
      /roster-invite/:token/:register is the route for a user who gets a roster invite email and clicks "I need to create an account" link
      this route will try to log them in if they actually do already have an account, if they do not they go to the
      login app at /login/signup/TALENT which is the same for as users who sign up directly
      */}
      <AuthenticatedRoute redirectToRegisterPage exact path="/roster-invite/:token/:register">
        <Page withoutNavigation>
          <RosterInvite fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/accept-profile-access/:token">
        <Page>
          <AcceptProfileAccess fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute redirectToRegisterPage exact path="/accept-profile-access/:token/:register">
        <Page>
          <AcceptProfileAccess fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      {/* create personal is where a user is sent after creating a repped profile or accepting a repped profile on a fresh artist */}
      <AuthenticatedRoute exact path="/artists/create-personal">
        <Page withoutNavigation>
          <ArtistsCreatePersonal fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/artists/create">
        <Page withoutNavigation>
          <ArtistsCreate fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      {/* when a user is accepting a roster invite to an existing account
      this page is where they select whether to create a new profiile or copy
      an existing one for the new repped profile */}
      <AuthenticatedRoute exact path="/artists/:artistId">
        <Page innerTheme="light" withoutNavigation>
          <ArtistsDetail fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/artists/:artistId/profiles">
        <Page innerTheme="light" withoutNavigation>
          <ArtistsProfiles fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/artists">
        <Page innerTheme="light" withoutNavigation>
          <ArtistsSelect fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/audition-invite/:requestId">
        <Page>
          <ScheduleInvite fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/callback-invite/:requestId">
        <Page>
          <ScheduleInvite fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/media-bank">
        <Page>
          <MediaBank fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/media-bank/:mediaType">
        <Page>
          <MediaBank fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/casting-billboard/saved-searches">
        <Page blueBg noMaxWidthOnContainer>
          <SavedSearches fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute path="/casting-billboard/:subNav?">
        <PageShell>
          <CastingBillboard fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/email-reset">
        <Page withoutNavigation>
          <EmailReset fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      {/* heap shows now uses of this route in the last year, should be safe to remove  */}
      <AuthenticatedRoute exact path="/loginAdmin">
        <Page>
          <LoginAdmin fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      {/* heap shows now uses of this route in the last year, should be safe to remove  */}
      <AuthenticatedRoute exact path="/register">
        <Page withoutNavigation>
          <Register fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/reset-email">
        <Page withoutNavigation>
          <ResetEmail fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/submissions">
        <Page blueBg noMaxWidthOnContainer>
          <Submissions fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/verify-email">
        <Page withoutNavigation>
          <VerifyEmail fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
      <AuthenticatedRoute exact path="/your-alerts">
        <PageShell>
          <YourAlerts fallback={<Loader />} />
        </PageShell>
      </AuthenticatedRoute>

      {submissionTrends && (
        <AuthenticatedRoute exact path="/your-insights">
          <Page>
            <Redirect to="/your-insights/submission-trends" />
          </Page>
        </AuthenticatedRoute>
      )}
      {!submissionTrends && yourAuditions && (
        <AuthenticatedRoute exact path="/your-insights">
          <Page>
            <Redirect to="/your-insights/your-auditions" />
          </Page>
        </AuthenticatedRoute>
      )}
      {yourAuditions && (
        <AuthenticatedRoute exact path="/your-insights/your-auditions">
          <Page blueBg>
            <YourAuditions fallback={<Loader />} />
          </Page>
        </AuthenticatedRoute>
      )}
      {submissionTrends && (
        <AuthenticatedRoute exact path="/your-insights/submission-trends">
          <Page>
            <SubmissionTrends />
          </Page>
        </AuthenticatedRoute>
      )}

      {roleTracker && (
        <AuthenticatedRoute exact path="/your-insights/role-tracker">
          <Page>
            <RoleTracker />
          </Page>
        </AuthenticatedRoute>
      )}

      <AuthenticatedRoute exact path="/edit-uploaded-photo">
        <Page withoutNavigation>
          <EditUploadedPhoto fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>

      <AuthenticatedRoute path="*">
        <Page>
          <NotFound fallback={<Loader />} />
        </Page>
      </AuthenticatedRoute>
    </Switch>
  );
};

export default Routes;
